import get from 'lodash.get';
import set from 'lodash.set';
import has from 'lodash.has';

// @ts-ignore
import search from 'javascript-array-search';

import {
    VirtualizedTableItem,
    VirtualizedTableColumnKeys
} from 'services/virtualizedTable/types';
import {
    selectVirtualizedTableColumnsArray,
    selectVirtualizedTableColumnsKeyedByKey,
    selectVirtualizedTableColumnOptionsKeyedByKey,
    selectVirtualizedTableItemArray
} from 'services/virtualizedTable/selectors';
import {
    selectQueryString,
    makeSelectQueryStringValue
} from 'workspaces/react-router-selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
// @ts-ignore
import orderBy from 'utils/custom-orderBy';
import filterByEntities from 'utils/filter-by-entities';
import filterByParsedQueryString from 'utils/filter-by-parsed-query-string';

import { FIELD_TYPE_DROPDOWN } from './constants';

/**
 * Mutate items to include dropdown option text for use in filtering and ordering.
 */
const selectItemArrayWithDropdownOptionText = createDeepEqualSelector(
    [
        selectVirtualizedTableItemArray,
        selectVirtualizedTableColumnOptionsKeyedByKey
    ],
    (itemArray, virtualizedTableColumnOptionsKeyedByKey) => {
        const dropdownColumnKeys = Object.keys(
            virtualizedTableColumnOptionsKeyedByKey
        );

        return itemArray.map((item) => {
            // Clone the item
            const nextItem = { ...item };

            /**
             * For each dropdown column find the item's value's corresponding option text and store it against them cloned item.
             */
            dropdownColumnKeys.forEach((columnKey) => {
                const itemValue = get(nextItem, columnKey);
                const optionText = get(
                    virtualizedTableColumnOptionsKeyedByKey,
                    [columnKey, itemValue, 'text']
                );
                set(
                    nextItem,
                    `${columnKey}-${FIELD_TYPE_DROPDOWN}`,
                    optionText
                );
            });

            return nextItem;
        });
    }
);

const selectItemArrayFilteredByEntities = createDeepEqualSelector(
    [
        selectItemArrayWithDropdownOptionText,
        makeSelectQueryStringValue('entities')
    ],
    (itemArrayWithDropdownOptionText, entityIds: string[] | undefined) =>
        filterByEntities(itemArrayWithDropdownOptionText, entityIds)
);

const selectFilteredItemArray = createDeepEqualSelector(
    [
        selectItemArrayFilteredByEntities,
        selectVirtualizedTableColumnsArray,
        selectQueryString
    ],
    (
        itemArrayFilteredByEntities,
        virtualizedTableColumnsArray,
        queryString
    ) => {
        const filters = {};

        // Build new filter from query string based on column config
        virtualizedTableColumnsArray.forEach((column) => {
            // TODO: refactor value to key
            const { value, queryStringFilterField } = column;

            if (queryStringFilterField) {
                const queryStringFilterValue = get(
                    queryString,
                    queryStringFilterField
                );
                set(filters, value, queryStringFilterValue);
            }
        });

        return filterByParsedQueryString(itemArrayFilteredByEntities, filters);
    }
);

const selectVirtualizedTableSearchableColumnKeys = createDeepEqualSelector(
    selectVirtualizedTableColumnsArray,
    (columns) =>
        columns.reduce((result, column) => {
            const updatedResult = [...result];

            if (!column.disableSearching) updatedResult.push(column.value);

            return updatedResult;
        }, [] as VirtualizedTableColumnKeys)
);

const selectSearchedItemArray = createDeepEqualSelector(
    [
        selectFilteredItemArray,
        makeSelectQueryStringValue('search'),
        selectVirtualizedTableSearchableColumnKeys,
        selectVirtualizedTableColumnOptionsKeyedByKey
    ],
    (
        filteredItemArray,
        queryStringValueSearch,
        columnKeys,
        virtualizedTableColumnOptionsKeyedByKey
    ) => {
        const dropdownColumnKeys = Object.keys(
            virtualizedTableColumnOptionsKeyedByKey
        );

        const searchFields = [...columnKeys];

        dropdownColumnKeys.forEach((columnKey) => {
            searchFields.push(`${columnKey}-${FIELD_TYPE_DROPDOWN}`);
        });

        return search(filteredItemArray, queryStringValueSearch, searchFields);
    }
);

const selectSortAttributes = createDeepEqualSelector(
    [makeSelectQueryStringValue('sort')],
    (sortState: 'name_asc' | 'name_desc' | undefined = 'name_asc') =>
        sortState.split('_')
);

const selectSortColumn = createDeepEqualSelector(
    [selectSortAttributes],
    (sortAttributes) => sortAttributes[0]
);

const selectSortDirection = createDeepEqualSelector(
    [selectSortAttributes],
    (sortAttributes) => (sortAttributes[1] !== 'desc' ? 'asc' : 'desc')
);

const selectOrderedItemArray = createDeepEqualSelector(
    [
        selectSearchedItemArray,
        selectSortColumn,
        selectSortDirection,
        selectVirtualizedTableColumnsKeyedByKey
    ],
    (
        searchedItemArray,
        sortColumn,
        sortDirection,
        virtualizedTableColumnsKeyedByKey
    ) => {
        const isDropdownColumn = has(virtualizedTableColumnsKeyedByKey, [
            sortColumn,
            'options'
        ]);

        const orderByField = isDropdownColumn
            ? `${sortColumn}-${FIELD_TYPE_DROPDOWN}`
            : sortColumn;

        return orderBy(
            searchedItemArray,
            [orderByField, 'key'],
            [sortDirection, 'asc']
        );
    }
);

const selectItemKeys = createDeepEqualSelector(
    selectOrderedItemArray,
    (orderedItemArray) =>
        orderedItemArray.map((item: VirtualizedTableItem) => item.key)
);

export {
    selectFilteredItemArray,
    selectSearchedItemArray,
    selectSortAttributes,
    selectSortColumn,
    selectSortDirection,
    selectOrderedItemArray,
    selectItemKeys
};
